<script setup lang="tsx">
import { ref, onMounted, onBeforeUnmount, computed, nextTick, watch } from 'vue'
import http from '@/services/http'
import type { Response, StatusData, Progress } from '@/models/api-generated'
import { useI18n } from 'vue-i18n'
import { PermOwnMeasureRead, PermAllMeasureRead } from '@/models/enums-generated'
import { getMethodStatusForProgress, getMethodStatusText, hasPermission } from '@/services/utils'

const { t } = useI18n()
const props = defineProps({
	h: String,
	s: String,
	m: String,
})
const currentUser = sessionMap.user
const result = ref<Progress>()
const canViewMethods = computed(() => hasPermission(PermOwnMeasureRead, PermAllMeasureRead))

const scrollContainer = ref<HTMLElement | null>(null)
const showLeft = ref(false)
const showRight = ref(false)

function updateArrows() {
	const el = scrollContainer.value
	if (!el) return
	const overshoot = 1
	const canScroll = el.scrollWidth > el.clientWidth
	showLeft.value = canScroll && el.scrollLeft > overshoot
	showRight.value = canScroll && el.scrollLeft + el.clientWidth < el.scrollWidth - overshoot

	// Safari fix: columns underline width
	const legend = el.querySelector('.columns.legend') as HTMLElement | null
	if (legend) {
		const w = legend.scrollWidth
		el.querySelectorAll('.columns').forEach((c) => {
			;(c as HTMLElement).style.setProperty('--columns-width', w + 'px')
		})
	}
}

function scrollLeft() {
	scrollContainer.value?.scrollBy({ left: -scrollContainer.value.clientWidth, behavior: 'smooth' })
}

function scrollRight() {
	scrollContainer.value?.scrollBy({ left: scrollContainer.value.clientWidth, behavior: 'smooth' })
}

const resizeObserver = new ResizeObserver(() => updateArrows())

onMounted(async () => {
	if (canViewMethods.value) {
		const { data } = await http.get<Response<Progress>>('api/dashboard/progress')
		result.value = data.data
	}

	await nextTick()
	const el = scrollContainer.value
	if (el) {
		el.addEventListener('scroll', updateArrows)
		resizeObserver.observe(el)
	}
	updateArrows()
})

onBeforeUnmount(() => {
	const el = scrollContainer.value
	if (el) {
		el.removeEventListener('scroll', updateArrows)
		resizeObserver.disconnect()
	}
})

watch(result, () => nextTick(updateArrows), { flush: 'post' })

function style(a?: number) {
	if (!a) a = 0
	return `flex-basis: ${a}%;`
}

function getBarClasses(status: string) {
	return {
		'column-bar': true,
		[`series-${status}`]: true,
		'stripe-effect': status === 'ci' || status === 'wni',
	}
}

function getTooltip(data: StatusData, status: string) {
	const s = getMethodStatusForProgress(status)
	return t('progressTooltip', { count: data.count || 0, percentage: data.percentage || 0, status: getMethodStatusText(s) || '' })
}

function goToMethodsPage(status: string, module: string[], securityNeed: string) {
	const params = new URLSearchParams()
	params.append('type', securityNeed)
	params.append('status', status)
	module.forEach((mod) => {
		params.append('module', mod)
	})
	window.location.href = `/app/methods?${params}`
}
</script>

<template>
	<p class="text-center fw-bold">
		{{ $t('dashboard.byModules') }}
		<template v-if="hasPermission(PermOwnMeasureRead) && !hasPermission(PermAllMeasureRead)">
			- {{ $t('user.label') }} {{ currentUser.firstname }} {{ currentUser.lastname }}
		</template>
		<template v-else-if="!canViewMethods"> - {{ $t('roles.missingRights') }}</template>
	</p>

	<div class="container">
		<div v-if="result">
			<div class="scroll-wrapper">
				<div v-show="showLeft" class="scroll-arrow left" @click="scrollLeft"><i class="bi bi-arrow-left-short"></i></div>

				<div ref="scrollContainer" class="scroll-area">
					<div class="progress-container">
						<div v-for="(label, key) in { high: props.h, standard: props.s, main: props.m }" :key="key" class="d-flex">
							<h4 class="align-self-center me-4 mb-0 flex55">
								<span class="badge badge-outline w-100">{{ label }}</span>
							</h4>
							<div class="columns">
								<div class="column-container" v-if="result.totalProgress && result.modules && result.modules.length">
									<div
										v-for="(value, status) in result.totalProgress.progress[key]"
										:key="status"
										:class="getBarClasses(status)"
										:style="`${style(value.percentage)}; cursor: pointer;`"
										@click="goToMethodsPage(getMethodStatusForProgress(status), [], key)"
										v-one-tooltip="getTooltip(value, status)"
									></div>
								</div>

								<div v-for="module in result.modules" :key="module.name" class="column-container">
									<div
										v-for="(value, status) in module.progress[key]"
										:key="status"
										:class="getBarClasses(status)"
										:style="`${style(value.percentage)}; cursor: pointer;`"
										@click="goToMethodsPage(getMethodStatusForProgress(status), module.subModules, key)"
										v-one-tooltip="getTooltip(value, status)"
									></div>
								</div>
							</div>
						</div>
					</div>

					<div class="d-flex">
						<div class="align-self-center me-4 mb-0 flex55"></div>
						<div class="progress-container">
							<div class="columns legend">
								<div class="column-container" v-if="result.totalProgress && result.modules && result.modules.length">
									<div class="column-bar">{{ $t('dashboard.total') }}</div>
								</div>

								<div v-for="module in result.modules" :key="module.name" class="column-container">
									<div class="column-bar">{{ module.name }}</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div v-show="showRight" class="scroll-arrow right" @click="scrollRight"><i class="bi bi-arrow-right-short"></i></div>
			</div>
		</div>
		<div v-else class="text-center">
			<br />
			<br />
			<br />
			<br />
			<br />
			<br />
			<div v-if="canViewMethods" class="spinner-border text-primary"></div>
		</div>
	</div>
</template>

<style lang="sass" scoped>
.container
  height: 330px

.flex55
  flex: 0 0 55px

.scroll-wrapper
  position: relative
  overflow: hidden

.scroll-area
  overflow-x: auto
  -webkit-overflow-scrolling: touch
  scroll-behavior: smooth
  position: relative
  z-index: 0

.columns
  display: inline-flex
  gap: 4px
  width: max-content
  position: relative

.columns::after
  content: ''
  position: absolute

  bottom: 0
  height: 1px
  width: var(--columns-width, 100%)
  background: var(--bs-secondary-color)
  pointer-events: none

.legend
  height: 30px

.column-container
  flex: 0 0 60px

.column-bar
  cursor: pointer
  transition: stroke-width .2s ease, stroke .2s ease, filter .2s ease
  color: var(--bs-secondary-color)

.column-bar:hover
  transform: scale(1.06)
  filter: brightness(1.2)
  box-shadow: 0 4px 10px rgba(0,0,0,.2)
  transition: all .2s ease-in-out

.scroll-arrow
  position: absolute
  top: 0
  width: 40px
  display: flex
  align-items: center
  justify-content: center
  background: rgba(0,0,0,.3)
  color: #fff
  font-size: 22px
  cursor: pointer
  user-select: none
  z-index: 10
  transition: background 0.2s

.scroll-arrow:hover
  background: rgba(0,0,0,.45)

.scroll-arrow.left
  left: 0

.scroll-arrow.right
  right: 0
</style>
